<script setup lang="ts">
  const { isApple } = useDevice();
  const route = useRoute()
  const config = useRuntimeConfig()
  const androidLink = 'https://play.google.com/store/apps/details?id=net.offlinefirst.smoxy&referrer=utm_source%3Dsmoxy%26utm_medium%3Dchallenges%26utm_campaign%3Dsmoxychallenges'
  const appStoreLink = 'https://apps.apple.com/us/app/smoxy-quit-smoking/id1511487101?pt=117785216&ct=smoxychallenges&mt=8'
  const store = isApple ? appStoreLink : androidLink

  const title = 'Quit Smoking Challenges - Smoxy'
  const description = 'Join monthly quit-smoking challenges with Smoxy and stay motivated with support from the community.'
  const canonicalUrl = `${config.public.hostname}${route.path}`
  const shareImage = `${config.public.hostname}/android-chrome-512x512.png`

  useSeoMeta({
    title,
    description,
    ogTitle: title,
    ogDescription: description,
    ogType: 'website',
    ogUrl: canonicalUrl,
    ogImage: shareImage,
    ogImageAlt: 'Smoxy quit smoking challenges',
    twitterCard: 'summary_large_image',
    twitterTitle: title,
    twitterDescription: description,
    twitterImage: shareImage,
    robots: 'index,follow',
  })

  useHead({
    link: [
      { rel: 'canonical', href: canonicalUrl },
    ],
  })
</script>

<template>
  <div class="min-h-screen bg-gradient-to-br from-[#452879] via-purple-800 to-[#3D3F58] text-white px-6 py-12 relative overflow-hidden">
    <!-- Decorative Circle -->
    <div class="absolute w-[768px] h-[768px] rounded-full bg-gradient-to-br from-[#7204FF] to-[#7204FF]/20 blur-3xl z-0" style="left: -384px; top: -384px;"></div>
    
    
    <div class="max-w-7xl mx-auto">
      <!-- Logo -->
      <div class="pb-12 relative z-10 flex flex-row items-center">
        <img
            alt="Smoxy logo"
            width="48" height="48"
            src="@/assets/logo_transparent.svg"
            class="w-12 h-12 p-1 mr-3 rounded-xl bg-primary-500" 
          />
          <SmoxyLogoType :class="{'fill-white': true}" />		
      </div>

      <!-- Main Content -->
      <div class="grid md:grid-cols-2 gap-0 items-center ">
        <!-- Left Side -->
        <div class="space-y-6 text-center md:text-left ">
          <!-- Rating -->
          <div class="flex items-center gap-2 justify-center md:justify-start">
            <div class="flex gap-1">
              <img src="@/assets/rating_star.svg" alt="star" class="w-6 h-6" />
              <img src="@/assets/rating_star.svg" alt="star" class="w-6 h-6" />
              <img src="@/assets/rating_star.svg" alt="star" class="w-6 h-6" />
              <img src="@/assets/rating_star.svg" alt="star" class="w-6 h-6" />
              <img src="@/assets/rating_star.svg" alt="star" class="w-6 h-6" />
              
            </div>
            <span class="text-md font-semibold text-purple-300">4.8</span>
          </div>

          <!-- Headline -->
          <h1 class="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
            Make This Month the<br />One You Quit for Good
          </h1>

          <!-- Description -->
          <p class="text-lg md:text-xl text-purple-200 max-w-lg mx-auto md:mx-0">
            Join the monthly quit-smoking challenge and get motivated by the Smoxy community.
          </p>

          <!-- Download Button & Store Badges -->
          <div class="flex flex-col sm:flex-row items-center gap-4 justify-center md:justify-start pt-6">
            <a :href="store" class="bg-purple-600 hover:bg-purple-500 text-white font-semibold px-8 py-4 rounded-full text-lg transition-colors">
              Download now
            </a>
            <div class="flex items-center gap-3">
              <a :href="androidLink" target="_blank" aria-label="Get it on Google Play" class="hover:scale-[99%]">
                <DownloadPlayStore class="text-white hover:text-white/80 h-10" /> 
              </a>
              <a :href="appStoreLink" aria-label="Download on the App Store" target="_blank" class="hover:scale-[99%]">
                <DownloadAppStore class="text-white hover:text-white/80 h-10"  />
              </a>
            </div>
          </div>
        </div>
<!-- Right Side -->
        <div class="flex justify-center md:justify-end ">
          <img
            src="@/assets/quit-smoking-challenges-cards.png"
            alt="Quit Smoking Challenge Cards"
            width="1966"
            height="1558"
            decoding="async"
            fetchpriority="high"
            class="w-full max-w-md md:max-w-4xl h-auto"
          />
        </div>
      </div>
    </div>
  </div>
</template>

